import {
  AddShoppingCart as AddToCartIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { searchProducts } from "../../../application/actions/supplierActions";
import { addToCart } from "../../../application/slices/orders";

const ProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const searchResults = useSelector(
    (state) => state.suppliers.searchResults.data
  );
  const loading = useSelector((state) => state.suppliers.searchResults.loading);
  const totalCount = useSelector(
    (state) => state.suppliers.searchResults.totalCount
  );

  // Local State
  const [filters, setFilters] = useState({
    searchTerm: "",
    category: "",
    minPrice: "",
    maxPrice: "",
    supplierId: "",
    isAvailable: "",
    sortBy: "createdAt",
    sortDescending: true,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 12,
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    handleSearch();
  }, [pagination.pageNumber]);

  const handleSearch = () => {
    const searchParams = {
      ...filters,
      ...pagination,
    };
    dispatch(searchProducts(searchParams));
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      pageNumber: value,
    });
    window.scrollTo(0, 0);
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: "",
      category: "",
      minPrice: "",
      maxPrice: "",
      supplierId: "",
      isAvailable: "",
      sortBy: "createdAt",
      sortDescending: true,
    });
    setPagination({ pageNumber: 1, pageSize: 12 });
  };

  const handleAddToCart = (product) => {
    dispatch(
      addToCart({
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity: 1,
      })
    );
    toast.success(`${product.name} به سبد خرید اضافه شد`);
  };

  const handleViewSupplier = (supplierId) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1;

  const categories = [
    "پنل خورشیدی",
    "اینورتر",
    "باتری",
    "شارژ کنترلر",
    "کابل و اتصالات",
    "سازه نگهدارنده",
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          محصولات خورشیدی
        </Typography>
        <Typography variant="body1" color="text.secondary">
          جستجو و خرید تجهیزات خورشیدی
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Box */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="جستجوی محصول..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange("searchTerm", "")}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid>

          {/* Filter Toggle */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? "پنهان کردن فیلترها" : "نمایش فیلترها"}
            </Button>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              جستجو
            </Button>
          </Grid>

          {/* Advanced Filters */}
          {showFilters && (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>دسته‌بندی</InputLabel>
                  <Select
                    value={filters.category}
                    label="دسته‌بندی"
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداقل قیمت (ریال)"
                  value={filters.minPrice}
                  onChange={(e) =>
                    handleFilterChange("minPrice", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداکثر قیمت (ریال)"
                  value={filters.maxPrice}
                  onChange={(e) =>
                    handleFilterChange("maxPrice", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>موجودی</InputLabel>
                  <Select
                    value={filters.isAvailable}
                    label="موجودی"
                    onChange={(e) =>
                      handleFilterChange("isAvailable", e.target.value)
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="true">موجود</MenuItem>
                    <MenuItem value="false">ناموجود</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>مرتب‌سازی</InputLabel>
                  <Select
                    value={filters.sortBy}
                    label="مرتب‌سازی"
                    onChange={(e) =>
                      handleFilterChange("sortBy", e.target.value)
                    }
                  >
                    <MenuItem value="createdAt">جدیدترین</MenuItem>
                    <MenuItem value="price">قیمت</MenuItem>
                    <MenuItem value="name">نام</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilters}
                >
                  پاک کردن فیلترها
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Products Grid */}
      {!loading && (
        <>
          {searchResults && searchResults.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {totalCount} محصول یافت شد
              </Typography>

              <Grid container spacing={3}>
                {searchResults.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={product.imageUrl || "/default-product.jpg"}
                        alt={product.name}
                        sx={{ objectFit: "cover" }}
                      />

                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom noWrap>
                          {product.name}
                        </Typography>

                        {product.brand && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            برند: {product.brand}
                          </Typography>
                        )}

                        <Box sx={{ mb: 2 }}>
                          {product.category && (
                            <Chip
                              label={product.category}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          )}
                          {product.isAvailable !== undefined && (
                            <Chip
                              label={product.isAvailable ? "موجود" : "ناموجود"}
                              color={product.isAvailable ? "success" : "error"}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {product.description}
                        </Typography>

                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {product.price?.toLocaleString("fa-IR")} ریال
                        </Typography>

                        {product.supplierName && (
                          <Button
                            size="small"
                            onClick={() =>
                              handleViewSupplier(product.supplierId)
                            }
                          >
                            تأمین‌کننده: {product.supplierName}
                          </Button>
                        )}
                      </CardContent>

                      <CardActions>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AddToCartIcon />}
                          onClick={() => handleAddToCart(product)}
                          disabled={!product.isAvailable}
                        >
                          افزودن به سبد
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                محصولی یافت نشد
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                لطفاً فیلترهای جستجوی خود را تغییر دهید
              </Typography>
            </Paper>
          )}

          {/* Pagination */}
          {searchResults && searchResults.length > 0 && totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={pagination.pageNumber}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default ProductList;
